import React, { useState } from 'react'
import PropTypes from 'prop-types'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import DialogTitle from '@material-ui/core/DialogTitle'
import Typography from '@material-ui/core/Typography'
import Dialog from 'components/dialog'
import { useDialogTitleStyles } from 'components/dialog/use-dialog-title-styles'
import ChevronLeftIcon from 'components/material-symbol/icons/ChevronLeft'
import ChevronRightIcon from 'components/material-symbol/icons/ChevronRight'
import { VIDEO_PLAYER_PROPS } from './utils'
import SwingVideoActions from './swing-video-actions'
import { useStyles } from './styles'
import VideoThumbnail from './video-thumbnail'

const getLabel = (index, count) => `Video ${index + 1} of ${count}`

// Several golfer videos as a thumbnail grid. A tile opens that video in a
// dialog (autoplaying player, Download and Analyze in Onform, previous/next).
// Closing the dialog unmounts the player, which stops playback.
const SwingVideoGallery = ({ videos, golfer, showAnalyze, trackedProps }) => {
  const classes = useStyles()
  const dialogClasses = useDialogTitleStyles()
  const [openIndex, setOpenIndex] = useState(null)
  const isOpen = openIndex !== null
  // Keep the last video while the dialog closes so it doesn't flash empty.
  const [shownIndex, setShownIndex] = useState(0)
  const index = isOpen ? openIndex : shownIndex
  const video = videos[index]
  const label = getLabel(index, videos.length)

  const open = nextIndex => {
    setShownIndex(nextIndex)
    setOpenIndex(nextIndex)
  }

  return (
    <>
      <Box className={classes.videoGrid}>
        {videos.map((tileVideo, tileIndex) => {
          const tileLabel = getLabel(tileIndex, videos.length)
          return (
            <Box key={`${tileVideo.url}-${tileIndex}`}>
              <button
                type="button"
                className={classes.videoTile}
                aria-label={`Play ${tileLabel}`}
                onClick={() => open(tileIndex)}
              >
                <VideoThumbnail src={tileVideo.thumbnailUrl} alt="" large />
              </button>
              <Typography
                className={`${classes.statTileLabel} ${classes.videoTileLabel}`}
                aria-hidden
              >
                {tileLabel}
              </Typography>
            </Box>
          )
        })}
      </Box>
      <Dialog
        isOpen={isOpen}
        onClose={() => setOpenIndex(null)}
        maxWidth="md"
        fullWidth
        showCloseButton
        classes={{ paper: dialogClasses.dialogPaper }}
        aria-labelledby="swing-video-dialog-title"
      >
        {() => (
          <>
            <DialogTitle
              disableTypography
              classes={{ root: dialogClasses.titleRoot }}
            >
              <Typography
                component="h2"
                id="swing-video-dialog-title"
                className={dialogClasses.title}
              >
                {label}
              </Typography>
            </DialogTitle>
            <Box className={dialogClasses.content}>
              {video ? (
                <>
                  {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                  <video
                    key={video.url}
                    className={classes.mediaVideo}
                    src={video.url}
                    poster={video.thumbnailUrl || undefined}
                    controls
                    autoPlay
                    {...VIDEO_PLAYER_PROPS}
                    playsInline
                    aria-label={`${golfer}'s swing video (${label})`}
                  />
                  <SwingVideoActions
                    video={video}
                    label={label}
                    showAnalyze={showAnalyze}
                    trackedProps={{ ...trackedProps, videoNumber: index + 1 }}
                  />
                </>
              ) : null}
            </Box>
            <Box className={classes.videoNav}>
              {index > 0 ? (
                <Button
                  color="primary"
                  classes={{ label: classes.buttonLabel }}
                  startIcon={<ChevronLeftIcon aria-hidden />}
                  onClick={() => open(index - 1)}
                >
                  Previous video
                </Button>
              ) : null}
              {index < videos.length - 1 ? (
                <Button
                  color="primary"
                  className={classes.videoNavNext}
                  classes={{ label: classes.buttonLabel }}
                  endIcon={<ChevronRightIcon aria-hidden />}
                  onClick={() => open(index + 1)}
                >
                  Next video
                </Button>
              ) : null}
            </Box>
          </>
        )}
      </Dialog>
    </>
  )
}

SwingVideoGallery.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      url: PropTypes.string.isRequired,
      thumbnailUrl: PropTypes.string,
    }),
  ).isRequired,
  golfer: PropTypes.string.isRequired,
  showAnalyze: PropTypes.bool,
  trackedProps: PropTypes.object,
}

export default SwingVideoGallery
